// The URL holds the open event (docs/ARCHITECTURE.md §2.7): /e/<id> is its share link. Opening an event's card
// adds a history entry, so Back closes the card (like the phone's swipe-down); moving from card to card replaces it.
// Loading a link opens that card, fetching the event (GET /events/<id>) if the feed doesn't hold it.
import { parseEvent, type RawEvent } from "../data/event";
import { eventById, feedStore } from "./feed";
import { closeSheet, moveMap, openSheet, viewStore } from "./view";

const BASE = import.meta.env.BASE_URL; // "/"
const eventPath = (id: string) => `${BASE}e/${encodeURIComponent(id)}`;
/** The event id in the address (/e/<id>), or null. */
export function idInPath(): string | null {
  const m = location.pathname.slice(BASE.length - 1).match(/^\/e\/([^/]+)\/?$/);
  return m ? decodeURIComponent(m[1]!) : null;
}
const keep = () => location.search + location.hash;

/** Resolves once the first events have arrived (or failed to). */
function feedLoaded() {
  if (feedStore.getState().source !== "loading") return Promise.resolve();
  return new Promise<void>((ok) => {
    const unsub = feedStore.subscribe((s) => { if (s.source !== "loading") { unsub(); ok(); } });
  });
}

async function openLinked(id: string) {
  // The feed usually has it: wait for its first answer before asking the server for this one event.
  if (!eventById(id)) await feedLoaded();
  let e = eventById(id);
  if (!e) {
    try {
      const res = await fetch(`/events/${encodeURIComponent(id)}`);
      if (res.ok) {
        e = parseEvent((await res.json()) as RawEvent);
        const linked = new Map(feedStore.getState().linked); linked.set(id, e);
        feedStore.setState({ linked });
      }
    } catch { /* offline: the card can't open */ }
  }
  if (!e || idInPath() !== id) return;
  openSheet({ kind: "event", id });
  moveMap({ lat: e.lat, lng: e.lng }, 15, true);
}

/** Starts the URL ↔ open card sync; returns a stop function. */
export function startUrlSync() {
  let ours = false; // the current history entry is one we pushed for a card
  const first = idInPath();
  if (first) void openLinked(first);
  const unsub = viewStore.subscribe((s, prev) => {
    if (s.sheet === prev.sheet) return;
    const id = s.sheet?.kind === "event" ? s.sheet.id : null, inPath = idInPath();
    if (id && id !== inPath) {
      if (inPath) history.replaceState({ nowishCard: true }, "", eventPath(id) + keep());
      else { history.pushState({ nowishCard: true }, "", eventPath(id) + keep()); ours = true; }
    } else if (!id && inPath) {
      // Closed in the page: go back off the card's entry if we made it, else just drop the id from the address.
      if (ours || history.state?.nowishCard) { ours = false; history.back(); }
      else history.replaceState(null, "", BASE + keep());
    }
  });
  const pop = () => {
    const id = idInPath();
    ours = !!id && !!history.state?.nowishCard;
    const open = viewStore.getState().sheet;
    if (!id) { if (open?.kind === "event") closeSheet(); }
    else if (!(open?.kind === "event" && open.id === id)) void openLinked(id);
  };
  addEventListener("popstate", pop);
  return () => { unsub(); removeEventListener("popstate", pop); };
}
